<template>
  <div class="indicators f-h f-w" :class="elClasses">
    <FiltersZone v-if="!hideHeader" :modal-title="$t('message.OPTIONS')">
      <div slot="shortcuts" class="v-centerer">
        <h2 v-if="asComponent" class="basic-sub-title indicators__page-title">
          {{ pageTitle }}
        </h2>
        <ChartPeriodSelector
          ref="periodSelector"
          v-model="displayConfig"
          :default-expanded="defaultPeriodsExpanded"
          :preset-id="presetId"
          :disable-date-switcher="editionMode"
          query-prefix="indicators"
          :hide-preset-field="$store.state.device.isSmartphonePortrait"
          show-default-wheel-view
          @expand="periodsExpanded"
          @presetPick="presetId = $event"
        />
      </div>

      <ComponentsList :value="idcOptions" @edit="paramsEdit" />
      <!-- Temporary patch for demo only -->
      <h2 class="basic-sub-title">
        {{ $t('message.EDIT_INDIC_VALUES') }}
      </h2>
      <button class="generic-border generic-border--text-btn generic-border--blue" @click="toggleEditionMode">
        <Icon>{{ editMedium }}</Icon>
        <span>{{ $t(editionMode ? 'message.STOP_EDIT_INDIC_VALUES' : 'message.EDIT_INDIC_VALUES') }}</span>
      </button>
      <!-- Temporary patch for demo only -->
      <h2 class="basic-sub-title">
        {{ $t('message.EXPORT_TO_EXCEL') }}
      </h2>
      <button class="generic-border generic-border--text-btn generic-border--blue" @click="exportIndicatorsToExcel">
        <Icon>{{ downloadMedium }}</Icon>
        <span>{{ $t('message.EXPORT_TO_EXCEL') }}</span>
      </button>
      <h2 v-if="currentUser && [11254, 11256].includes(currentUser.company)" class="basic-sub-title">
        Synchronisation
      </h2>
      <button
        v-if="currentUser && [11254, 11256].includes(currentUser.company)"
        class="generic-border generic-border--text-btn generic-border--blue"
        @click="leancureSynchronisation"
      >
        <Icon>{{ refreshMedium }}</Icon>
        <span>Leancure Sync</span>
      </button>
    </FiltersZone>
    <!-- Main content -->
    <div
      class="f-h-below-filters"
      :class="{ 'indicators__content--full-height': hideHeader }"
      :style="team ? 'margin:0px':''"
    >
      <div ref="indicatorThemes" class="indicators__themes">
        <div
          v-for="(theme, tIndex) in displayedThemes"
          :key="theme.id"
          class="indic-theme generic-border generic-border--card"
          :class="{ gray: theme.copyPublic > 0 }"
          :style="theme.style"
        >
          <h2 class="basic-sub-title indicators__themes__title" style="cursor: pointer;" @click="openWheel(theme.id)">
            {{ theme.name }}
            [{{ theme.letter }}]
          </h2>
          <div v-if="editionMode" class="indicators-wrapper">
            <IndicatorInput
              v-for="indicator in theme.indicators"
              ref="inputs"
              :key="indicator.id"
              class="f-w"
              :indicator="indicator"
              :min="true"
              :focused-date="range.end"
              :data-id="indicator.id"
              shown
              :focus-on-mount="indicator.id == editionInputFocus"
              hide-theme-name
              minimal-date
              @clickPrevDate="prevDate(indicator)"
              @clickNextDate="nextDate(indicator)"
            />
          </div>
          <div
            v-else-if="computedDisplayConfig && computedDisplayConfig.id === INDICATOR_WHEEL_VIEW_PRESET.id"
            class="indicators-wrapper generic-border"
          >
            <Wheel
              :theme="theme"
              :value="indicatorWheelDate"
              hide-controls
              @input="indicatorWheelDate = $event"
            />
          </div>
          <div v-else class="indicators-wrapper">
            <!-- :class attr is used by IAAnalyser -->
            <Indicator
              v-for="(indicator, iIndex) in theme.indicators"
              ref="graphs"
              :key="indicator.id"
              class="f-w"
              :class="`indicator-${tIndex}-${iIndex}`"
              :indicator="indicator"
              :style="indicator.style"
              :range="allValuesRange"
              :visible-chart-range="visibleValuesRange"
              :disabled="team != null || !!mgtThemes"
              :edition-mode="editionMode"
              :custom-config="computedDisplayConfig"
              :refreshing="!!refreshingPerId[`${indicator.id}`]"
              :cumulated="!!mgtThemes"
              :working-days="mgtWorkingDays ? mgtWorkingDays : undefined"
              :team-ids="mgtTeamIds ? mgtTeamIds : undefined"
              @nameClicked="displayAnalysis(tIndex, iIndex, true)"
              @changePeriod="selectConfigFromUnit"
              @chartClicked="$emit('chartClicked', indicator)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden components -->
    <!-- Wheel -->
    <transition name="fade" mode="out-in">
      <div v-if="wheelTheme" class="dark-overlay v-centerer" @click="closeWheel">
        <div class="dark-overlay-content indicators__wheel-modal" @click.stop="">
          <CloseButton @click.native="closeWheel" />
          <h1 class="basic-blue-title indicators__wheel-modal__header">
            {{ wheelTheme.name }}
            [{{ wheelTheme.letter }}]
            {{ $t('message.TWO_DOTS') }}
            {{ indicatorWheelDate.format('MMM YYYY') }}
          </h1>
          <div class="indicators__wheel-modal__container">
            <Wheel
              :theme="wheelTheme"
              :value="indicatorWheelDate"
              @input="indicatorWheelDate = $event"
            />
          </div>
        </div>
      </div>
    </transition>
    <!-- Hide indicator options -->
    <HideIndicatorsOptions
      v-model="idcOptHiddenIndicators"
      :displayed="showHideOptions"
      :themes="dClassicThemes"
      @close="showHideOptions = false"
    />
  </div>
</template>

<script>
/**
 * Indicators page component
 *
 * @vue-prop {Number} [team] - Using the indicators component from another page component
 * @vue-prop {Object} [mgtDisplayConfig] - Indicator display configuration. If set, use that configuration
 *                                         instead of the component's config.
 * @vue-prop {Array} [mgtThemes] - Indicator themes. If specified, use these themes and don't fetch any from the server
 * @vue-prop {Array} [mgtTeamIds] - IDs of the teams to compute from the Indicator components when specifying mgtThemes
 * @vue-prop {Number} [customThemesMargin=0.9] - Margin in rem between theme columns
 * @vue-prop {Number} [customIndicMargin=0.9] - Margin in rem between indicator rows
 * @vue-prop {Number} [maxIndicatorsInCol=4] - Number of indicators that may appear in a column before scrolling
 *
 */
import $ from 'jquery';
import { cloneDeep, isEqual, uniq, omit } from 'lodash';
// Vuex
import {
  FETCH_INDICATOR_THEMES,
  FETCH_INDICATOR,
  FETCH_INDICATOR_WARNINGS,
} from '@/store/action-types';
import { GET_INDICATOR_THEMES } from '@/store/mutation-types';
import { INDICATOR_WHEEL_VIEW_PRESET } from '@/store/getters';
// Components
import ChartPeriodSelector from '@/components/chart-period-selector.vue';
import CloseButton from '@/components/close-button.vue';
import ComponentsList from '@/components/components-list.vue';
import HideIndicatorsOptions from '@/components/hide-indicators-options.vue';
import Icon from '@/components/icon.vue';
import Indicator from '@/components/indicator.vue';
import IndicatorInput from '@/components/indicator-input.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Wheel from '@/components/wheel.vue';
// Mixins
import mixinIdcOptions from '@/mixins/page-options/indicators.vue';
// Socket utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Utils
import IAAnalyser from '@/utils/classes/IAAnalyser';
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { TIME_RANGE_FORMAT, WHEEL_RANGE_FORMAT } from '@/utils/constants';
import { calcPeriodStartDate, calert } from '@/utils/main';
import { computeConfigStart, exportIndicatorsToExcel } from '@/utils/indicator-utils';
import {
  refreshMedium,
  downloadMedium,
  editMedium,
} from '@/assets/icons';


export default {

  name: 'Indicators',

  components: {
    ChartPeriodSelector,
    CloseButton,
    ComponentsList,
    HideIndicatorsOptions,
    Icon,
    Indicator,
    IndicatorInput,
    FiltersZone,
    Wheel,
  },

  mixins: [mixinIdcOptions, mixinSocketHandler],

  props: {
    team: {
      type: Object,
      default: null,
    },
    mgtDisplayConfig: {
      type: Object,
      default: undefined,
    },
    mgtThemes: {
      type: Array,
      default: undefined,
    },
    mgtWorkingDays: {
      type: Array,
      default: undefined,
    },
    mgtTeamIds: {
      type: Array,
      default: undefined,
    },
    mgtHiddenIndicators: {
      type: Array,
      default: undefined,
    },
    asComponent: Boolean,
    hideHeader: Boolean,
    customThemesMargin: {
      type: Number,
      default: 0.7,
    },
    customIndicMargin: {
      type: Number,
      default: 1,
    },
    minIndicatorsInCol: {
      type: Number,
      default: 3,
    },
    maxIndicatorsInCol: {
      type: Number,
      default: 4,
    },
  },

  data() {
    return {
      INDICATOR_WHEEL_VIEW_PRESET,
      downloadMedium,
      editMedium,
      refreshMedium,
      // Indicator data
      demoThemes: [],
      refreshTimeout: null,
      indicatorIdsToRefesh: [],
      destroyed: false,
      // Formatted as : { '<indic_id>': <is_indicator_refreshing>, ... }
      refreshingPerId: {},
      displayConfig: this.$store.getters.defaultIndicDisplayConfig,
      // Update comps
      wheelTheme: null,
      editionInputFocus: '',
      maxIndicatorsInColumn: 5,
      // Default height of the period selector (including the page title)
      periodSelectorHeight: 60,
      // Modal window to hide indicators from the page
      showHideOptions: false,
      // Intelligent assistant
      analysisDaysRange: 30,
      // Prevent fetching indicators in mixin
      doFetchIndicatorsInMixin: false,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.INDICATORS'),
      iaAnalysisBtns: [{
        text: this.$t('message.IA_ANALYSIS'),
        action: () => {
          this.App.iaOverlay.hide();
          this.displayAnalysis(0, 0, false);
        },
      }],
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('INDICATORS'), this.teacherBtn('RECURRENCE')],
    };
  },

  computed: {
    /** String: Text used by assistant overlay. See src/views/readme.md */
    iaAnalysisText() {
      return this.$store.state.indicatorAnalysisMsg;
    },
    /** Object: CSS classes applied to this.$el */
    elClasses() {
      return {
        'indicators--as-component': this.asComponent,
        'indicators--editing': this.editionMode,
      };
    },
    themes() {
      return this.$store.state[GET_INDICATOR_THEMES.stateKey] || [];
    },
    dThemes() {
      if (this.mgtThemes) {
        return cloneDeep(this.mgtThemes);
      }
      if (this.team) {
        return this.team.themes;
      }
      return this.demoMode ? this.demoThemes : this.themes;
    },
    /** Array: All themes excluding GW themes and their indicators */
    dClassicThemes() {
      return this.dThemes.filter(theme => !theme.indicators.some(i => Number(i.isGw) === 1));
    },
    /** Array: Filtered themes and indicators according to hidden indicators */
    displayedThemes() {
      // Filter themes and indicators according to hidden indicators
      const displayedThemes = [];
      const maxIndicatorsInCol = Math.min(this.maxIndicatorsInCol, this.maxIndicatorsInColumn);
      for (let i = 0, len = this.dThemes.length; i < len; i++) {
        const refTheme = this.dThemes[i];
        const theme = { ...refTheme, indicators: [] };
        for (let j = 0, jlen = refTheme.indicators.length; j < jlen; j++) {
          const indic = refTheme.indicators[j];
          if (!this.idcOptHiddenIndicators.includes(indic.id)) {
            theme.indicators.push(indic);
          }
        }
        if (theme.indicators.length) {
          displayedThemes.push(theme);
        }
      }

      // Set CSS style of each column / element according to the number of indicators in each theme
      let wperc = 100 / displayedThemes.length;
      if (this.$store.state.device.isSmartphonePortrait) {
        wperc = 100;
      } else if (this.$store.state.device.isSmartphoneLandscape) {
        wperc = 50;
      } else if (displayedThemes.length >= 6) {
        wperc = 16.6666;
      }
      const maxIndicators = displayedThemes
        .reduce((max, theme) => (max > theme.indicators.length ? max : theme.indicators.length), 0);
      const divider = maxIndicators.sBetweenBoundaries(this.minIndicatorsInCol, maxIndicatorsInCol);
      const hperc = 100 / divider;

      displayedThemes.forEach((theme) => {
        // Set the theme's style here
        const iLen = theme.indicators.length;
        const percHeight = iLen * hperc > 100 ? 100 : iLen * hperc;
        theme.style = {
          flex: `0 0 calc(${wperc}% - ${this.customThemesMargin}rem)`,
          /* eslint-disable-next-line no-nested-ternary */
          height: this.$store.state.device.isSmartphoneLandscape
            ? `calc(${iLen * 200}px + ${(3.6 + iLen)}rem)`
            : iLen * hperc > 100 ? `${percHeight}%` : `calc(((100% - 3.6rem) / ${divider}) * ${iLen} + 3.6rem)`,
          minWidth: '0px',
          marginBottom: this.$store.state.device.isSmartphoneLandscape ? '1rem' : undefined,
        };
        if (this.editionMode) {
          theme.style.height = 'auto';
        }
        theme.indicators.forEach((indicator) => {
          const iperc = iLen >= maxIndicatorsInCol ? 100 / maxIndicatorsInCol : 100 / iLen;
          indicator.style = this.editionMode || this.$store.state.device.isSmartphoneLandscape
            ? { height: '200px' }
            : {
              height: `calc(${iperc}% - ${this.customIndicMargin}rem - 1px)`,
              marginBottom: `${this.customIndicMargin}rem`,
            };
        });
      });
      return displayedThemes;
    },
    /** Whether the periods section should be fuly visible by default */
    defaultPeriodsExpanded() {
      return this.$route.query.periodsExpanded === 'true';
    },
    /** Object: Range of chart's visible values */
    range() {
      return this.visibleValuesRange;
    },
    /** Object: Range of chart's visible values */
    visibleValuesRange() {
      if (!this.computedDisplayConfig) {
        return null;
      }
      const { chartPeriodObj, pointPeriodObj, chartEnd } = this.computedDisplayConfig;
      return {
        start: calcPeriodStartDate(chartEnd, chartPeriodObj, pointPeriodObj),
        end: chartEnd.clone().endOf('day'),
      };
    },
    /**
     * Object: Range of all values, including values not displayed.
     * Especially useful when building a cumulated chart (where increasingValues > 0).
     * eg: Values should be cumulated each month from the 1st of each month
     *     => need the values from the 1st day of month
     */
    allValuesRange() {
      if (!this.computedDisplayConfig) {
        return null;
      }
      if (!this.computedDisplayConfig.increasingValues) {
        // No increasing values, no need for an extended range
        return this.visibleValuesRange;
      }
      const { chartEnd, configStart } = computeConfigStart(this.computedDisplayConfig, this.visibleValuesRange.start);
      return { start: this.$moment(configStart), end: chartEnd };
    },
    /** Array: Array of all indicators of the team */
    allIndicators() {
      return this.themes.flatMap(theme => theme.indicators);
    },
    /** Array: Array of indicator IDs to fetch after filtering using hiddenIndicators */
    filteredIndicatorIds() {
      return this.allIndicators
        .filter(indic => !this.idcOptHiddenIndicators.includes(indic.id))
        .map(indic => indic.id);
    },
    /**
     * Display config of indicator charts. Controls the period of chart points,
     * and the overall period of the charts as well as a few options.
     * See chart-period-selector.vue to learn more.
     * It may be set from a parent component if Indicator component is not used as a page.
     * @return {Object}
     */
    computedDisplayConfig() {
      return this.mgtDisplayConfig || this.displayConfig;
    },
    /** Number: ID of the reset to pass to the side options */
    idcOptPresetId() {
      return this.computedDisplayConfig.id;
    },
    /**
     * ============
     * Query string
     * ============
     */
    editionMode: {
      get() {
        return this.$route.query.editionMode === 'true';
      },
      set(newValue) {
        const query = Object.assign({}, this.$route.query);
        query.editionMode = String(!!newValue);
        if (this.$route.query.editionMode !== query.editionMode) {
          this.$router.replace({ query });
        }
      },
    },
    /** Indicator config ID */
    presetId: {
      get() {
        const { presetId, defaultPresetId } = this.$route.query;
        if (presetId === null || isNaN(presetId)) {
          // No default preset in query, use deprecated indicatorsView param
          if (defaultPresetId === null || isNaN(defaultPresetId)) {
            return this.currentTeam ? this.currentTeam.indicatorsView : 0;
          }
          return defaultPresetId === null || isNaN(defaultPresetId) ? 1 : Number(defaultPresetId);
        }
        return Number(presetId);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        if (newVal !== Number(query.presetId)) {
          query.presetId = newVal;
          this.$router.replace({ query });
        }
      },
    },
    /** Array: of indicator IDs which won't be displayed on this page */
    idcOptHiddenIndicators: {
      get() {
        if (this.mgtHiddenIndicators) {
          return this.mgtHiddenIndicators;
        }
        const { hiddenIndicators } = this.$route.query;
        if (hiddenIndicators === undefined) {
          return this.idcOptGwIndicIds;
        }
        return uniq([
          ...hiddenIndicators.split(',').filter(n => n !== '').map(id => Number(id)), ...this.idcOptGwIndicIds,
        ]);
      },
      set(ids) {
        const query = Object.assign({}, this.$route.query);
        // WARNING: Do not delete the query param if empty as the default value is different than the empty value
        query.hiddenIndicators = ids.join(',');
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Number: ID of the theme to display as a wheel */
    indicatorWheelId: {
      get() {
        const { indicatorWheelId } = this.$route.query;
        return indicatorWheelId ? Number(indicatorWheelId) : 0;
      },
      set(newValue) {
        const query = Object.assign({}, this.$route.query);
        if (!newValue) {
          delete query.indicatorWheelId;
        } else {
          query.indicatorWheelId = Number(newValue);
        }
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    indicatorWheelDate: {
      get() {
        if (this.$route.query.indicatorWheelDate) {
          return this.$moment(this.$route.query.indicatorWheelDate);
        }
        return this.$moment(this.allValuesRange.end).endOf('month');
      },
      set(newValue) {
        const query = Object.assign({}, this.$route.query);
        if (newValue === null || !newValue.format) {
          delete query.indicatorWheelDate;
        } else {
          query.indicatorWheelDate = newValue.format(WHEEL_RANGE_FORMAT);
        }
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    editionMode(isEditing) {
      if (!isEditing) {
        this.refresh();
      }
    },
    computedDisplayConfig(newValue, oldValue) {
      if (!isEqual(omit(newValue, 'chartEnd'), omit(oldValue, 'chartEnd'))) {
        this.refresh();
      }
    },
    idcOptHiddenIndicators(newValue, oldValue) {
      if (!isEqual(newValue, oldValue)) {
        this.refresh();
      }
    },
    range(newValue, oldValue) {
      // Trigger refresh only if range change
      if (
        !newValue.start.isSame(oldValue.start, 'd')
        || !newValue.end.isSame(oldValue.end, 'd')
      ) {
        this.refresh();
      }
    },
    indicatorWheelId() {
      this.fetchWheelData();
    },
    indicatorWheelDate() {
      this.fetchWheelData();
    },
  },

  created() {
    // First fetch all themes and indicators without values, then fetch filtered indicators with values
    this.$store.dispatch(FETCH_INDICATOR_THEMES).then(() => { this.refresh(); });
    // Socket handlers
    this.handleSocketMessage(SocketMessages.CC_MSGS.INDICATORS, (msg) => {
      this.handleIndicatorSocketMessage(Number(msg.id));
    });
    this.handleSocketMessage(SocketMessages.CC_MSGS.PUBLIC_INDICATOR, (msg) => {
      this.handleIndicatorSocketMessage(Number(msg.id));
    });
  },

  mounted() {
    $(window).on(this.resizeEventName, () => {
      this.periodSelectorHeight = this.$refs.periodSelector.$el.offsetHeight;
      if (!this.editionMode) {
        // Redraw charts
        this.resizeCharts();
      }
      this.sizeUpdate();
    });
    this.sizeUpdate();
  },

  beforeDestroy() {
    this.destroyed = true;
  },

  methods: {
    /** Handle the reception of a socket message regarding an indicator. Refresh that indicator and related */
    handleIndicatorSocketMessage(id) {
      const indicators = this.allIndicators
        .filter((indic) => {
          const isTheOne = Number(indic.id) === id;
          const isCalculatedFrom = (indic.calcItems || []).some(cItem => cItem.calculatedIndicator === id);
          return isTheOne || isCalculatedFrom;
        })
        .map(indic => indic.id);
      if (indicators) {
        this.indicatorIdsToRefesh.push(...indicators);
        this.refresh(indicators);
      }
    },
    /**
     * Refresh the indicators' data and values
     * @param  {Array} [specificIndicatorIds]  If set, refresh only these indicators
     */
    refresh(specificIndicatorIds) {
      if (this.demoMode || this.mgtThemes) {
        return;
      }
      const { start, end } = this.allValuesRange;
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout);
        if (specificIndicatorIds) {
          // If a refresh request was about to start, include the ids of said request to the current one
          specificIndicatorIds = [...specificIndicatorIds, ...(this.indicatorIdsToRefesh || [])];
        }
      }
      const idParam = specificIndicatorIds || this.filteredIndicatorIds;
      const params = {
        values: true,
        range: {
          start: start.startOf('day').format(TIME_RANGE_FORMAT),
          end: this.$moment(end).endOf('day').format(TIME_RANGE_FORMAT),
        },
        indicatorId: idParam,
      };
      this.refreshTimeout = setTimeout(() => {
        this.indicatorIdsToRefesh = this.indicatorIdsToRefesh.filter(id => !idParam.includes(id));
        params.indicatorId.forEach((indicId) => {
          this.$set(this.refreshingPerId, `${indicId}`, true);
        });
        // Set a minimum of 30 calendar days for analysis
        if (end.diff(start, 'd') < 30) {
          params.range.start = end.clone().subtract(30, 'd').startOf('day').format(TIME_RANGE_FORMAT);
        }
        params.requireAnalysis = true;
        this.$store.dispatch(FETCH_INDICATOR, params)
          .then(() => {
            params.indicatorId.forEach((indicId) => {
              this.refreshingPerId[`${indicId}`] = false;
            });
            // Fetch related warnings
            this.$store.dispatch(FETCH_INDICATOR_WARNINGS);
          })
          .catch(() => {
            params.indicatorId.forEach((indicId) => {
              this.refreshingPerId[`${indicId}`] = false;
            });
          });
      }, 250);
      // Indicator wheel theme
      this.fetchWheelData();
    },
    /** Display Neo analysis for the indicator with given index */
    displayAnalysis(themeIndex, indicatorIndex, preventFetchingNext) {
      IAAnalyser.displayIndicatorsAnalysis(this.themes, themeIndex, indicatorIndex, preventFetchingNext);
    },
    sizeUpdate() {
      const height = this.$refs.indicatorThemes.offsetHeight;
      if (height < 250) {
        this.maxIndicatorsInColumn = 1;
      } else if (height < 400) {
        this.maxIndicatorsInColumn = 2;
      } else if (height < 550) {
        this.maxIndicatorsInColumn = 3;
      } else if (height < 650) {
        this.maxIndicatorsInColumn = 4;
      } else {
        this.maxIndicatorsInColumn = 5;
      }
    },
    /** Periods section has been (un-)expanded */
    periodsExpanded(expanded) {
      const query = Object.assign({}, this.$route.query);
      query.periodsExpanded = expanded ? 'true' : 'false';
      if (query.periodsExpanded !== this.$route.query.periodsExpanded) {
        this.$router.replace({ query });
      }
      this.periodSelectorHeight = this.$refs.periodSelector.$el.offsetHeight;
      this.$nextTick(this.resizeCharts);
    },
    /** Click prev btn when editing values */
    prevDate(indicator) {
      const date = this.displayConfig.chartEnd.clone();
      // If input period isn't daily, offset to the previous period
      if (indicator.inputPeriodUnit === 'w') {
        date.startOf('week');
      } else if (indicator.inputPeriodUnit === 'M') {
        date.startOf('month');
      }
      const wd = this.currentTeam.workingDays || [true, true, true, true, true, true, true];
      do {
        date.subtract(1, 'd');
      } while (!wd[date.isoWeekday() - 1]);
      this.displayConfig = { ...this.displayConfig, chartEnd: date };
      this.editionInputFocus = indicator.id;
    },
    /** Click next btn when editing values */
    nextDate(indicator) {
      const date = this.displayConfig.chartEnd.clone();
      // If input period isn't daily, offset to the next period
      if (indicator.inputPeriodUnit === 'w') {
        date.endOf('week');
      } else if (indicator.inputPeriodUnit === 'M') {
        date.endOf('month');
      }
      const wd = this.currentTeam.workingDays || [true, true, true, true, true, true, true];
      do {
        date.add(1, 'd');
      } while (!wd[date.isoWeekday() - 1]);
      this.displayConfig = { ...this.displayConfig, chartEnd: date };
      this.editionInputFocus = indicator.id;
    },
    resizeCharts() {
      const { doughnuts, graphs } = this.$refs;
      if (doughnuts) {
        doughnuts.forEach((comp) => {
          comp.build();
        });
      }
      if (graphs) {
        graphs.forEach((comp) => {
          comp.resizeChart();
        });
      }
    },
    /** Select a config matching the point period unit */
    selectConfigFromUnit(evt, unit) {
      const preset = this.$store.getters.indicDisplayConfigs.find(config => config.pointPeriodObj.unit === unit);
      if (preset) {
        this.presetId = preset.id;
      }
    },
    /**
     * Open wheel modal - date can be set externally with wheelDate
     * @param {number} themeId - Theme ID of the theme that will be displayed in the wheel
     */
    openWheel(themeId) {
      if (!(themeId > 0)) return;
      this.indicatorWheelId = themeId;
      this.indicatorWheelDate = this.range.end;
      $(document).on(`escKeyup.${this.uuid}_${themeId}`, this.closeWheel);
    },
    closeWheel() {
      $(document).off(`escKeyup.${this.uuid}_${this.indicatorWheelId}`);
      this.wheelTheme = null;
      this.indicatorWheelId = null;
      this.indicatorWheelDate = null;
    },
    /**
     * Fetch theme data for wheel display
     * @param {number} themeId - Theme ID of the theme that will be fetch
     */
    fetchWheelData() {
      if (!(this.indicatorWheelId > 0)) return;
      const start = this.indicatorWheelDate.clone().startOf('month').format(TIME_RANGE_FORMAT);
      const end = this.indicatorWheelDate.clone().endOf('month').format(TIME_RANGE_FORMAT);
      const params = {
        values: true,
        range: { start, end },
        themeId: this.indicatorWheelId,
        teamId: (this.team ? this.team.id : undefined),
      };
      this.$store.dispatch(FETCH_INDICATOR_THEMES, params).then((data) => {
        if (data instanceof Array) {
          ([this.wheelTheme] = data);
        }
      });
    },
    /** Set params from the options componentsList defined in page-options/indicators */
    paramsEdit(option) {
      if (option.key === 'defaultPresetId') {
        this.presetId = Number(option.value);
      } else if (option.key === 'hiddenIndicators') {
        this.idcOptHiddenIndicators = option.value;
      }
    },
    toggleEditionMode() {
      this.editionMode = !this.editionMode;
    },
    doShowHideOptions() {
      this.showHideOptions = true;
    },
    /** Create a downloadable excel file to export the values of indicators */
    exportIndicatorsToExcel() {
      const { doughnuts, graphs } = this.$refs;
      const components = doughnuts && doughnuts.length ? doughnuts : graphs;
      const indicators = [];
      if (components) {
        for (let i = 0, len = components.length; i < len; i++) {
          if (components[i].stateIndicator && components[i].chartConfig && components[i].chartConfig.points) {
            indicators.push({ ...components[i].stateIndicator, points: components[i].chartConfig.points });
          } else {
            calert(this.$t('message.WAIT_INDICATORS_LOADED'));
            break;
          }
        }
      }
      exportIndicatorsToExcel(indicators);
    },
    createHelpSteps() {
      this.demoThemes = IAHelp.demoIndicators(this, 250);
      this.wheelDate = this.$moment().startOf('month');

      const previousDisplayConfig = Object.assign({}, this.displayConfig);
      // Set default config
      const defaultConfig = Object.assign({}, this.displayConfig);
      defaultConfig.pointPeriodObj = { period: 1, unit: 'd', startDay: 1 };
      defaultConfig.chartPeriodObj = { period: 10, unit: 'd' };
      this.displayConfig = defaultConfig;

      return [
        new IAHelpStep(null, this.$t('help.INDICS_INTRO')),
        new IAHelpStep('.indicator', this.$t('help.INDICS_CHART')),
        new IAHelpStep('.chart-period-selector .presets', this.$t('help.INDICS_PERIOD_PRESETS')),
        new IAHelpStep('.chart-period-selector .presets', this.$t('help.INDICS_PERIOD_POINT'), () => {
          const displayConfig = Object.assign({}, this.displayConfig);
          displayConfig.pointPeriodObj = { period: 1, unit: 'w', startDay: 1 };
          displayConfig.chartPeriodObj = { period: 5, unit: 'w' };
          this.displayConfig = displayConfig;
        }),
        new IAHelpStep('.indicator', this.$t('help.INDICS_PERIOD_POINT_WEEK')),
        new IAHelpStep('.chart-period-selector .title-range', this.$t('help.INDICS_PERIOD_DATE')),
        new IAHelpStep('.indic-theme .theme-letter', this.$t('help.INDICS_WHEEL_CLICK'), () => {
          [this.wheelTheme] = this.demoThemes;
        }),
        new IAHelpStep('.wheel', this.$t('help.INDICS_WHEEL'), () => {
          this.wheelTheme = null;
        }),
        new IAHelpStep('.indicator', this.$t('help.INDICS_CHART_CLICK'), () => {
          // Reset the previously set config
          this.displayConfig = previousDisplayConfig;
        }),
      ];
    },
    /**
     * Temporary patch for demo only
     * Create or update indicators based on the data found on the demo Leancure installation
     */
    leancureSynchronisation() {
      this.$http.post('/server/api/customerLinks/leancureDemo/sync.php')
        .then(() => {
          this.$store.dispatch(FETCH_INDICATOR_THEMES).then(() => { this.refresh(); });
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
@import "@/assets/scss/generic-border.scss";

.indic-theme.gray {
  background: #eee;
}

$header-line-height: 28px;
$header-v-padding: 15px;
.indicators {

  .basic-gray-section {
    overflow: hidden;
  }

  .f-h-below-filters {
    padding: $page-content-padding;
  }

  &__themes {
    &__title {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  &--as-component {
    background: white;
    border-radius: $main-border-radius;

    .filters-zone__buttons {
      border-bottom: 1px solid $color-gray-border;
      border-top-left-radius: $main-border-radius;
      border-top-right-radius: $main-border-radius;
    }

    .indic-theme {
      border: 1px solid $color-gray-border;
    }

    .indicators__page-title.basic-sub-title {
      margin-right: 1rem;
    }

    .indicators__themes__title.basic-sub-title {
      font-size: 1.1rem;
    }
  }

  &__content {
    &--full-height {
      &.f-h-below-filters {
        height: 100%;
      }
    }
  }

  &__themes {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
  }

  .indic-theme {

    .indicators__themes__title {
      margin-left: $page-content-padding;

      &:after {
        content: " ";
        display: block;
        background: $color-body-background;
        height: 1px;
        transform: translateY(1rem);
        margin-left: #{-$page-content-padding};
      }
    }

    .indicators-wrapper {
      height: calc(100% - calc(2 * #{$h2-margin} + #{$h2-line-height}));
      padding: 0 $page-content-padding;
      box-sizing: border-box;
      overflow: auto;

      .indicator {
        transition: box-shadow 0.3s;
        position: relative;
        margin-bottom: 1rem;

        &:after {
          content: " ";
          display: block;
          background: $color-body-background;
          width: calc(100% + #{2 * $page-content-padding});
          height: 1px;
          transform: translateY(0.5rem);
          margin: 0 #{-$page-content-padding} 1rem #{-$page-content-padding};
        }
      }

      .indicator-input {
        position: relative;

        &:after {
          content: " ";
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: $color-gray-background;
        }
      }
    }
  }

  .indicators__wheel-modal {
    &.dark-overlay-content {
      max-height: 95%;
      height: auto;
    }

    &__header {
      background-color: transparent;
      line-height: $header-line-height;
      padding: $header-v-padding 0;
      margin: 0;
    }

    &__container {
      height: calc(100% - #{$header-v-padding * 2 + $header-line-height});
    }
  }

  &--editing {
    .indic-theme {
      .indicators-wrapper {
        padding: 0;
      }
    }
  }
}

/** Mobile layout */
@media (max-width: $smartphone-landscape) {
  .indicators {
    $header-line-height: 28px;
    $header-v-padding: 7px;

    &__themes {
      flex-wrap: wrap;

      .indic-theme {
        height: auto;
      }
    }

    .indicators__wheel-modal__header {
      line-height: $header-line-height;
      padding: $header-v-padding 0;
    }

    .indicators__wheel-modal__container {
      height: calc(100% - #{$header-v-padding * 2 + $header-line-height});
    }
  }
}
</style>
